import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import type { MembershipType } from '@court-sync/shared';
import {
  membershipTypeDrawerFormSchema,
  membershipTypeFormToCreatePayload,
  membershipTypeFormToUpdatePayload,
  type MembershipTypeDrawerFormValues,
} from '@court-sync/shared';
import {
  Button,
  Drawer,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@/components/ui';
import { clubMembersService } from '@/services/clubMembers';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';

const TYPE_FORM_ID = 'membership-type-form';

/** Empty / unset input → null, otherwise parse as a number. For optional numeric fields. */
export function numberOrNull(value: unknown): number | null {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

/** Empty input → 0, otherwise parse as a number. For required numeric fields that default to 0. */
function numberOrZero(value: string): number {
  return value === '' ? 0 : Number(value);
}

interface MembershipTypeDrawerProps {
  clubId: string;
  currency: string;
  membershipType: MembershipType | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onCreatePanelOpen?: () => void;
  showCreateTourHelp?: boolean;
  showBackdrop?: boolean;
}

export function toFormValues(membershipType: MembershipType | null): MembershipTypeDrawerFormValues {
  if (!membershipType) {
    return {
      name: '',
      description: '',
      price: 0.01,
      billingInterval: 'month',
      courtHireDiscountPercent: 0,
      isActive: true,
      termType: 'recurring',
      periodAnchor: null,
      periodMonths: null,
      maxAdults: 1,
      includedJuniors: 0,
      extraJuniorsFree: false,
      maxTotalSeats: null,
      maxAgeAtJoin: null,
      minAgeAtJoin: null,
      requiresProof: false,
      proofLabel: '',
      requiresLinkedJunior: false,
      isPlaying: true,
      isPublic: false,
    };
  }

  return {
    name: membershipType.name,
    description: membershipType.description ?? '',
    price: membershipType.priceCents / 100,
    billingInterval: membershipType.billingInterval,
    courtHireDiscountPercent: membershipType.courtHireDiscountPercent,
    isActive: membershipType.isActive,
    termType: membershipType.termType,
    periodAnchor: membershipType.periodAnchor ?? null,
    periodMonths: membershipType.periodMonths ?? null,
    maxAdults: membershipType.maxAdults,
    includedJuniors: membershipType.includedJuniors,
    extraJuniorsFree: membershipType.extraJuniorsFree,
    maxTotalSeats: membershipType.maxTotalSeats ?? null,
    maxAgeAtJoin: membershipType.maxAgeAtJoin ?? null,
    minAgeAtJoin: membershipType.minAgeAtJoin ?? null,
    requiresProof: membershipType.requiresProof,
    proofLabel: membershipType.proofLabel ?? '',
    requiresLinkedJunior: membershipType.requiresLinkedJunior,
    isPlaying: membershipType.isPlaying,
    isPublic: membershipType.isPublic,
  };
}

export function resolveMembershipTypeCurrency(
  membershipType: MembershipType | null,
  createCurrency: string
): string {
  return membershipType?.currency ?? createCurrency;
}

function SectionHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {description ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      ) : null}
    </div>
  );
}

export function MembershipTypeDrawer({
  clubId,
  currency: createCurrency,
  membershipType,
  isOpen,
  onClose,
  onSaved,
  onCreatePanelOpen,
  showCreateTourHelp = false,
  showBackdrop = true,
}: MembershipTypeDrawerProps) {
  const isEditing = membershipType != null;
  const effectiveCurrency = resolveMembershipTypeCurrency(membershipType, createCurrency);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MembershipTypeDrawerFormValues>({
    resolver: zodResolver(membershipTypeDrawerFormSchema),
    defaultValues: toFormValues(membershipType),
  });

  const termType = useWatch({ control, name: 'termType' });
  const periodAnchor = useWatch({ control, name: 'periodAnchor' });
  const includedJuniors = useWatch({ control, name: 'includedJuniors' });
  const requiresProof = useWatch({ control, name: 'requiresProof' });

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(membershipType));
    }
  }, [isOpen, membershipType, reset]);

  const onSubmit = async (values: MembershipTypeDrawerFormValues) => {
    const payload = isEditing
      ? membershipTypeFormToUpdatePayload(values, effectiveCurrency)
      : membershipTypeFormToCreatePayload(values, effectiveCurrency);

    const res = isEditing
      ? await clubMembersService.updateMembershipType(clubId, membershipType.id, payload)
      : await clubMembersService.createMembershipType(clubId, payload);

    if (res.success) {
      toast.success(isEditing ? 'Membership plan updated' : 'Membership plan created');
      onSaved();
      onClose();
    } else {
      toast.error(
        res.error || (isEditing ? 'Failed to update membership plan' : 'Failed to create membership plan'),
      );
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      showBackdrop={showBackdrop}
      title={isEditing ? 'Edit membership plan' : 'Create membership plan'}
      description="Set price, term, covered seats, and eligibility rules for this plan."
      width="md"
      panelId={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
      onPanelOpen={!isEditing ? onCreatePanelOpen : undefined}
      headerActions={
        showCreateTourHelp ? (
          <DrawerTourHelp
            tourKey="club-members-type-create"
            suggestion="New to membership plans? Tour this form before you save."
            buttonLabel="Tour this form"
          />
        ) : undefined
      }
      footer={
        <div
          id={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
          className="flex justify-end gap-2"
        >
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={TYPE_FORM_ID} isLoading={isSubmitting}>
            {isEditing ? 'Save plan' : 'Create plan'}
          </Button>
        </div>
      }
    >
      <form id={TYPE_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div id={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.DETAILS : undefined}>
        {isEditing ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Price and billing changes apply to new subscriptions only. Existing members keep their current
            billing until cancelled.
          </p>
        ) : null}

        <Field data-invalid={!!errors.name?.message}>
          <FieldLabel htmlFor="membership-type-name">Name</FieldLabel>
          <Input
            id="membership-type-name"
            placeholder="e.g. Family membership"
            aria-invalid={!!errors.name?.message}
            {...register('name')}
          />
          <FieldError>{errors.name?.message}</FieldError>
        </Field>

        <Field>
          <FieldLabel htmlFor="membership-type-description">Description</FieldLabel>
          <Textarea
            id="membership-type-description"
            rows={3}
            placeholder="Optional benefits summary"
            {...register('description')}
          />
        </Field>

        <Field data-invalid={!!errors.price?.message}>
          <FieldLabel htmlFor="membership-type-price">Price ({effectiveCurrency})</FieldLabel>
          <Input
            id="membership-type-price"
            type="number"
            min={0.01}
            step={0.01}
            placeholder="0.00"
            aria-invalid={!!errors.price?.message}
            {...register('price')}
          />
          {isEditing ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Currency: {effectiveCurrency} (fixed for this plan)
            </p>
          ) : null}
          <FieldError>{errors.price?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.courtHireDiscountPercent?.message}>
          <FieldLabel htmlFor="membership-type-discount">Court hire discount (%)</FieldLabel>
          <Input
            id="membership-type-discount"
            type="number"
            min={0}
            max={100}
            step={0.01}
            aria-invalid={!!errors.courtHireDiscountPercent?.message}
            {...register('courtHireDiscountPercent', { valueAsNumber: true })}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Not yet applied to court hire pricing — stored for when member discounts launch.
          </p>
          <FieldError>{errors.courtHireDiscountPercent?.message}</FieldError>
        </Field>
        </div>

        {/* ── Term ─────────────────────────────────────────────────────── */}
        <div id={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.TERM : undefined}>
        <SectionHeading
          title="Term"
          description="How and when this plan expires."
        />

        <Field data-invalid={!!errors.termType?.message}>
          <FieldLabel htmlFor="membership-type-term">Term type</FieldLabel>
          <Controller
            name="termType"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="membership-type-term">
                  <SelectValue placeholder="Select term type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recurring">Recurring (ongoing subscription)</SelectItem>
                  <SelectItem value="fixed_period">Fixed period (season or fixed months)</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <FieldError>{errors.termType?.message}</FieldError>
        </Field>

        {termType === 'recurring' ? (
          <Field data-invalid={!!errors.billingInterval?.message}>
            <FieldLabel htmlFor="membership-type-interval">Billing interval</FieldLabel>
            <Controller
              name="billingInterval"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="membership-type-interval">
                    <SelectValue placeholder="Select interval" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError>{errors.billingInterval?.message}</FieldError>
          </Field>
        ) : (
          <>
            <Field data-invalid={!!errors.periodAnchor?.message}>
              <FieldLabel htmlFor="membership-type-period-anchor">Expires</FieldLabel>
              <Controller
                name="periodAnchor"
                control={control}
                render={({ field }) => (
                  <Select value={field.value ?? undefined} onValueChange={field.onChange}>
                    <SelectTrigger id="membership-type-period-anchor">
                      <SelectValue placeholder="Select how this plan expires" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="season">At the end of the club membership year</SelectItem>
                      <SelectItem value="join_date">A fixed number of months after joining</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <FieldError>{errors.periodAnchor?.message}</FieldError>
            </Field>

            {periodAnchor === 'join_date' ? (
              <Field data-invalid={!!errors.periodMonths?.message}>
                <FieldLabel htmlFor="membership-type-period-months">Number of months</FieldLabel>
                <Input
                  id="membership-type-period-months"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 3"
                  aria-invalid={!!errors.periodMonths?.message}
                  {...register('periodMonths', { setValueAs: numberOrNull })}
                />
                <FieldError>{errors.periodMonths?.message}</FieldError>
              </Field>
            ) : null}
          </>
        )}
        </div>

        {/* ── Seats ────────────────────────────────────────────────────── */}
        <div id={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.SEATS : undefined}>
        <SectionHeading
          title="Covered seats"
          description="Who this plan covers — set 0 adults for a junior-only plan, or add adults for shared Couple/Family plans."
        />

        <div className="grid grid-cols-2 gap-4">
          <Field data-invalid={!!errors.maxAdults?.message}>
            <FieldLabel htmlFor="membership-type-max-adults">Adults covered</FieldLabel>
            <Input
              id="membership-type-max-adults"
              type="number"
              min={0}
              step={1}
              aria-invalid={!!errors.maxAdults?.message}
              {...register('maxAdults', { setValueAs: numberOrZero })}
            />
            <FieldError>{errors.maxAdults?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.includedJuniors?.message}>
            <FieldLabel htmlFor="membership-type-included-juniors">Juniors included</FieldLabel>
            <Input
              id="membership-type-included-juniors"
              type="number"
              min={0}
              step={1}
              aria-invalid={!!errors.includedJuniors?.message}
              {...register('includedJuniors', { setValueAs: numberOrZero })}
            />
            <FieldError>{errors.includedJuniors?.message}</FieldError>
          </Field>
        </div>

        {includedJuniors > 0 ? (
          <div className="flex items-start gap-3">
            <Controller
              name="extraJuniorsFree"
              control={control}
              render={({ field }) => (
                <Switch
                  id="membership-type-extra-juniors-free"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <label htmlFor="membership-type-extra-juniors-free" className="cursor-pointer text-sm">
              <span className="font-medium text-gray-900 dark:text-white">
                Further juniors free
              </span>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Any additional juniors beyond the included count are covered at no extra charge
                (e.g. a Family plan covering unlimited children).
              </p>
            </label>
          </div>
        ) : null}

        <Field data-invalid={!!errors.maxTotalSeats?.message}>
          <FieldLabel htmlFor="membership-type-max-total-seats">Max total seats (optional)</FieldLabel>
          <Input
            id="membership-type-max-total-seats"
            type="number"
            min={1}
            step={1}
            placeholder="Leave blank for unbounded"
            aria-invalid={!!errors.maxTotalSeats?.message}
            {...register('maxTotalSeats', { setValueAs: numberOrNull })}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Hard cap on the total people covered. Leave blank when juniors are unlimited.
          </p>
          <FieldError>{errors.maxTotalSeats?.message}</FieldError>
        </Field>
        </div>

        {/* ── Eligibility ──────────────────────────────────────────────── */}
        <div id={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.ELIGIBILITY : undefined}>
        <SectionHeading
          title="Eligibility"
          description="Rules enforced when a member is added to this plan."
        />

        <div className="grid grid-cols-2 gap-4">
          <Field data-invalid={!!errors.minAgeAtJoin?.message}>
            <FieldLabel htmlFor="membership-type-min-age">Min age at join</FieldLabel>
            <Input
              id="membership-type-min-age"
              type="number"
              min={0}
              step={1}
              placeholder="No minimum"
              aria-invalid={!!errors.minAgeAtJoin?.message}
              {...register('minAgeAtJoin', { setValueAs: numberOrNull })}
            />
            <FieldError>{errors.minAgeAtJoin?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.maxAgeAtJoin?.message}>
            <FieldLabel htmlFor="membership-type-max-age">Max age at join</FieldLabel>
            <Input
              id="membership-type-max-age"
              type="number"
              min={0}
              step={1}
              placeholder="e.g. 17 for Junior"
              aria-invalid={!!errors.maxAgeAtJoin?.message}
              {...register('maxAgeAtJoin', { setValueAs: numberOrNull })}
            />
            <FieldError>{errors.maxAgeAtJoin?.message}</FieldError>
          </Field>
        </div>

        <div className="flex items-start gap-3">
          <Controller
            name="requiresProof"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-type-requires-proof"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-type-requires-proof" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Requires proof (e.g. Concession)</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Online purchases land in a pending-verification queue until staff confirm the proof.
            </p>
          </label>
        </div>

        {requiresProof ? (
          <Field data-invalid={!!errors.proofLabel?.message}>
            <FieldLabel htmlFor="membership-type-proof-label">Proof label</FieldLabel>
            <Input
              id="membership-type-proof-label"
              placeholder="e.g. Concession card"
              aria-invalid={!!errors.proofLabel?.message}
              {...register('proofLabel')}
            />
            <FieldError>{errors.proofLabel?.message}</FieldError>
          </Field>
        ) : null}

        <div className="flex items-start gap-3">
          <Controller
            name="requiresLinkedJunior"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-type-requires-linked-junior"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-type-requires-linked-junior" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">
              Requires a linked junior (e.g. Parent)
            </span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Only available to a parent or guardian of an existing junior member.
            </p>
          </label>
        </div>

        <div className="flex items-start gap-3">
          <Controller
            name="isPlaying"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-type-is-playing"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-type-is-playing" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Playing membership</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Turn off for non-playing plans (e.g. Associate/social membership).
            </p>
          </label>
        </div>
        </div>

        {/* ── Visibility ───────────────────────────────────────────────── */}
        <div id={!isEditing ? MEMBERSHIP_TYPE_CREATE_TOUR_STEP_IDS.VISIBILITY : undefined}>
        <SectionHeading title="Visibility" />

        <div className="flex items-start gap-3">
          <Controller
            name="isPublic"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-type-is-public"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-type-is-public" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Show on public join page</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Visitors can see and purchase this plan online once online join is enabled in club settings.
            </p>
          </label>
        </div>

        <div className="flex items-start gap-3">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-type-active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-type-active" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Active</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Inactive plans cannot be assigned to new members.
            </p>
          </label>
        </div>
        </div>
      </form>
    </Drawer>
  );
}
